import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Plus, QrCode, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function currencyFmt(amt, ccy = 'USD') {
  const n = Number(amt ?? 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: ccy,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

export default function EventsTab({ groupId }) {
  const { t } = useTranslation();

  const [events, setEvents] = useState([]);
  const [group, setGroup] = useState(null);

  const [showEventDialog, setShowEventDialog] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_date: '',
    location: '',
    price: 0,
    capacity: '',
  });


const [editingEventId, setEditingEventId] = useState(null);

  const openCreateEvent = () => {
    setEditingEventId(null);
    setEventForm({
      title: '',
      description: '',
      event_date: '',
      location: '',
      price: 0,
      capacity: '',
    });
    setShowEventDialog(true);
  };

  const openEditEvent = (ev) => {
    if (!ev) return;
    setEditingEventId(ev.id);
    const dtLocal = ev.event_date
      ? new Date(ev.event_date).toISOString().slice(0, 16)
      : '';
    setEventForm({
      title: ev.title || '',
      description: ev.description || '',
      event_date: dtLocal,
      location: ev.location || '',
      price: ev.price ?? 0,
      capacity: ev.capacity ?? '',
    });
    setShowEventDialog(true);
  };

  const [validateEventId, setValidateEventId] = useState(null);
  const [showValidateDialog, setShowValidateDialog] = useState(false);
  const [codeToValidate, setCodeToValidate] = useState('');

  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const scannerRef = useRef(null);

  const runFetch = useCallback(
    async (signal) => {
      try {
        const [g, e] = await Promise.all([
          api.get(`/groups/${groupId}`),
          api.get(`/groups/${groupId}/events`),
        ]);
        if (signal?.aborted) return;
        setGroup(g.data || null);
        setEvents(e.data || []);
      } catch {
        if (!signal?.aborted) {
          toast.error(t('groupAdminTabs.events.toastLoadError'));
        }
      }
    },
    [groupId, t]
  );

  useEffect(() => {
    const controller = new AbortController();
    const id = setTimeout(() => {
      runFetch(controller.signal);
    }, 0);
    return () => {
      controller.abort();
      clearTimeout(id);
    };
  }, [runFetch]);

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
            const payload = {
        ...eventForm,
        event_date: eventForm.event_date
          ? new Date(eventForm.event_date).toISOString()
          : null,
        price: Number(eventForm.price || 0),
        capacity: eventForm.capacity !== '' && eventForm.capacity != null
          ? parseInt(eventForm.capacity, 10)
          : null,
      };

      if (editingEventId) {
        await api.put(`/groups/${groupId}/events/${editingEventId}`, payload);
        toast.success(
          t('groupAdminTabs.events.toastUpdateSuccess', {
            defaultValue: 'Event updated',
          })
        );
      } else {
        await api.post(`/groups/${groupId}/events`, payload);
        toast.success(t('groupAdminTabs.events.toastCreateSuccess'));
      }
            setShowEventDialog(false);
      setEditingEventId(null);
      setEventForm({
        title: '',
        description: '',
        event_date: '',
        location: '',
        price: 0,
        capacity: '',
      });
      const controller = new AbortController();
      runFetch(controller.signal);
    } catch {
      toast.error(
        editingEventId
          ? t('groupAdminTabs.events.toastUpdateError', {
              defaultValue: 'Failed to update event',
            })
          : t('groupAdminTabs.events.toastCreateError')
      );
    }
  };

  const handleValidateTicket = async () => {
    const trimmed = codeToValidate.trim();
    const eventId = validateEventId;
    if (!eventId || !trimmed) return;
    try {
      const { data } = await api.post(
        `/events/${eventId}/validate-ticket`,
        {
          code: trimmed,
        }
      );
      if (data?.ok) {
        toast.success(
          data?.message || t('groupAdminTabs.events.toastValid')
        );
      } else {
        toast.error(
          data?.message || t('groupAdminTabs.events.toastInvalid')
        );
      }
      setCodeToValidate('');
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          t('groupAdminTabs.events.toastValidationError')
      );
    }
  };

  const startScan = async () => {
    try {
      setScanning(true);
      const { default: QrScanner } = await import('qr-scanner');

      scannerRef.current = new QrScanner(
        videoRef.current,
        (result) => onQrDetected(result?.data || result),
        { highlightScanRegion: true, maxScansPerSecond: 8 }
      );
      await scannerRef.current.start();
    } catch (e) {
      console.error(e);
      toast.error(t('groupAdminTabs.events.toastCameraError'));
      setScanning(false);
    }
  };

  const stopScan = () => {
    if (scannerRef.current) {
      try {
        scannerRef.current.stop();
        scannerRef.current.destroy();
      } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const onQrDetected = async (text) => {
    let payload = null;
    try {
      payload = JSON.parse(text);
    } catch {
      /* not JSON */
    }

    if (payload && payload.code) {
      if (payload.event_id) setValidateEventId(String(payload.event_id));
      setCodeToValidate(String(payload.code));
    } else {
      setCodeToValidate(String(text).trim());
    }

    const eventId = payload?.event_id
      ? String(payload.event_id)
      : validateEventId;
    const code = payload?.code ? String(payload.code) : codeToValidate;

    if (eventId && code) {
      stopScan();
      try {
        const { data } = await api.post(
          `/events/${eventId}/validate-ticket`,
          {
            code: code.trim(),
          }
        );
        if (data?.ok) {
          toast.success(
            data?.message || t('groupAdminTabs.events.toastValid')
          );
        } else {
          toast.error(
            data?.message || t('groupAdminTabs.events.toastInvalid')
          );
        }
        setCodeToValidate('');
      } catch (err) {
        toast.error(
          err?.response?.data?.detail ||
            t('groupAdminTabs.events.toastValidationError')
        );
      }
    }
  };

  useEffect(() => {
    if (!showValidateDialog) stopScan();
  }, [showValidateDialog]);

  const currency = group?.currency || 'USD';

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">
          {t('groupAdminTabs.events.title')}
        </h2>
        <div className="flex items-center gap-2">
          <Select
            value={validateEventId || undefined}
            onValueChange={setValidateEventId}
            disabled={events.length === 0}
          >
            <SelectTrigger className="w-56">
              <SelectValue
                placeholder={
                  events.length
                    ? t('groupAdminTabs.events.eventPlaceholder')
                    : t('groupAdminTabs.events.noEventsAvailable')
                }
              />
            </SelectTrigger>
            {events.length > 0 && (
              <SelectContent>
                {events.map((ev) => (
                  <SelectItem key={ev.id} value={ev.id}>
                    {ev.title}
                  </SelectItem>
                ))}
              </SelectContent>
            )}
          </Select>

          <Button
            variant="outline"
            onClick={() => setShowValidateDialog(true)}
            title={
              !validateEventId
                ? t('groupAdminTabs.events.validateTicketTip')
                : t('groupAdminTabs.events.validateTicket')
            }
          >
            <QrCode size={16} className="mr-2" />
            {t('groupAdminTabs.events.validateTicket')}
          </Button>

          <Button onClick={openCreateEvent}>
            <Plus size={16} className="mr-2" />
            {t('groupAdminTabs.events.addEvent')}
          </Button>
        </div>
      </div>

      {events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            {t('groupAdminTabs.events.noEvents')}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((ev) => (
            <Card key={ev.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle>{ev.title}</CardTitle>
                    <CardDescription>
                  {ev.event_date
                    ? format(new Date(ev.event_date), 'PPP p')
                    : '—'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => openEditEvent(ev)}
                      title={t('groupAdminTabs.events.edit', { defaultValue: 'Edit' })}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={async () => {
                        const ok = window.confirm(
                          t('groupAdminTabs.events.deleteConfirm', {
                            defaultValue: 'Delete this event?',
                          })
                        );
                        if (!ok) return;
                        try {
                          await api.delete(`/groups/${groupId}/events/${ev.id}`);
                          toast.success(
                            t('groupAdminTabs.events.toastDeleteSuccess', {
                              defaultValue: 'Event deleted',
                            })
                          );
                          const controller = new AbortController();
                          runFetch(controller.signal);
                        } catch (e) {
                          toast.error(
                            e?.response?.data?.detail ||
                              t('groupAdminTabs.events.toastDeleteError', {
                                defaultValue: 'Failed to delete event',
                              })
                          );
                        }
                      }}
                      title={t('groupAdminTabs.events.delete', { defaultValue: 'Delete' })}
                    >
                      <Trash2 size={16} />
                    </Button>
                    </div>
                  </div>
              </CardHeader>
              <CardContent className="text-sm text-slate-600">
                <p className="mb-2">{ev.description}</p>
                {ev.location && (
                  <p>
                    <strong>
                      {t('groupAdminTabs.events.location')}:
                    </strong>{' '}
                    {ev.location}
                  </p>
                )}
                <p className="mt-2">
                  <strong>
                    {t('groupAdminTabs.events.price')}:
                  </strong>{' '}
                  {Number(ev.price || 0) > 0
                    ? currencyFmt(
                        ev.price,
                        ev.currency || currency
                      )
                    : t('groupAdminTabs.events.free')}
                </p>
                {ev.capacity && (
                  <p className="mt-1">
                    <strong>
                      {t('groupAdminTabs.events.capacity')}:
                    </strong>{' '}
                    {ev.capacity}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('groupAdminTabs.events.dialogCreateTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('groupAdminTabs.events.dialogCreateDescription')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEvent} className="space-y-3">
            <div>
              <Label>{t('groupAdminTabs.events.fieldTitle')}</Label>
              <Input
                value={eventForm.title}
                onChange={(e) =>
                  setEventForm((f) => ({
                    ...f,
                    title: e.target.value,
                  }))
                }
                required
              />
            </div>
            <div>
              <Label>
                {t('groupAdminTabs.events.fieldDescription')}
              </Label>
              <Textarea
                value={eventForm.description}
                onChange={(e) =>
                  setEventForm((f) => ({
                    ...f,
                    description: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <Label>{t('groupAdminTabs.events.fieldDate')}</Label>
              <Input
                type="datetime-local"
                value={eventForm.event_date}
                onChange={(e) =>
                  setEventForm((f) => ({
                    ...f,
                    event_date: e.target.value,
                  }))
                }
                required
              />
            </div>
            <div>
              <Label>
                {t('groupAdminTabs.events.fieldLocation')}
              </Label>
              <Input
                value={eventForm.location}
                onChange={(e) =>
                  setEventForm((f) => ({
                    ...f,
                    location: e.target.value,
                  }))
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>
                  {t('groupAdminTabs.events.fieldPrice')}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={eventForm.price}
                  onChange={(e) =>
                    setEventForm((f) => ({
                      ...f,
                      price: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <Label>
                  {t('groupAdminTabs.events.fieldCapacity')}
                </Label>
                <Input
                  type="number"
                  value={eventForm.capacity}
                  onChange={(e) =>
                    setEventForm((f) => ({
                      ...f,
                      capacity: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEventDialog(false)}
              >
                {t('groupAdminTabs.events.cancel')}
              </Button>
              <Button type="submit">
                {t('groupAdminTabs.events.create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Validate Ticket Dialog */}
      <Dialog open={showValidateDialog} onOpenChange={setShowValidateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('groupAdminTabs.events.dialogValidateTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('groupAdminTabs.events.dialogValidateDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('groupAdminTabs.events.fieldEvent')}</Label>
              <Select
                value={validateEventId || undefined}
                onValueChange={setValidateEventId}
                disabled={events.length === 0}
              >
                <SelectTrigger className="w-full mt-1">
                  <SelectValue
                    placeholder={
                      events.length
                        ? t('groupAdminTabs.events.eventPlaceholder')
                        : t(
                            'groupAdminTabs.events.noEventsAvailable'
                          )
                    }
                  />
                </SelectTrigger>
                {events.length > 0 && (
                  <SelectContent>
                    {events.map((ev) => (
                      <SelectItem key={ev.id} value={ev.id}>
                        {ev.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                )}
              </Select>
            </div>
            <div>
              <Label>
                {t('groupAdminTabs.events.ticketCode')}
              </Label>
              <Input
                placeholder="e.g. EVTX-AB12CD34"
                value={codeToValidate}
                onChange={(e) =>
                  setCodeToValidate(e.target.value)
                }
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={scanning ? 'secondary' : 'outline'}
                onClick={() =>
                  scanning ? stopScan() : startScan()
                }
                disabled={!validateEventId && events.length === 0}
              >
                <QrCode size={16} className="mr-2" />
                {scanning
                  ? t('groupAdminTabs.events.stopScanning')
                  : t('groupAdminTabs.events.scanQR')}
              </Button>
              <span className="text-xs text-slate-500">
                {t('groupAdminTabs.events.tipQR')}
              </span>
            </div>

            {scanning && (
              <div className="rounded-xl overflow-hidden border">
                <video
                  ref={videoRef}
                  className="w-full aspect-video bg-black"
                  muted
                  playsInline
                />
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowValidateDialog(false)}
              >
                {t('groupAdminTabs.events.close')}
              </Button>
              <Button
                onClick={handleValidateTicket}
                disabled={
                  !validateEventId ||
                  !codeToValidate.trim()
                }
              >
                {t('groupAdminTabs.events.validate')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
